import type { Ctx } from '@milkdown/kit/ctx';
import { editorViewCtx } from '@milkdown/kit/core';
import type { EditorView } from '@milkdown/kit/prose/view';
import { renderToStaticMarkup } from 'react-dom/server';
import { SquaresFour } from '@phosphor-icons/react';

import { insertBlockAtCursor } from '../../plugins/utils';
import { getNodeAtCursor } from '../../utils/getNodeAtCursor';
import { VARIABLE_CONTENT_NODE_NAME } from './constants';
import { focusVariantDomBody, getVariantDomAtCursor } from './keymap';
import { createVariableContentBlock } from './utils';

export type SlashMenuItemConfig = {
  label: string;
  icon: string;
  onRun: (ctx: Ctx) => void;
};

/** Hide “Variable content” slash item when the cursor is already inside one. */
export function shouldShowVariableContentSlashItem(view: EditorView | null | undefined): boolean {
  if (!view) {
    return true;
  }

  return getNodeAtCursor(view, VARIABLE_CONTENT_NODE_NAME) == null;
}

export const VariableContentSlashMenuItems = {
  variableContent: (): SlashMenuItemConfig => ({
    label: 'Variable content',
    icon: renderToStaticMarkup(<SquaresFour size={20} weight="fill" />),
    onRun: (ctx: Ctx) => {
      const view = ctx.get(editorViewCtx);
      // Schema already forbids nesting; skip insert when already inside a block.
      if (getNodeAtCursor(view, VARIABLE_CONTENT_NODE_NAME)) {
        return;
      }

      const blockNode = createVariableContentBlock(view.state.schema);
      if (!blockNode) {
        return;
      }

      // Nesting is variable_content → content_variant → paragraph (offset 3), not block → paragraph (default 2).
      insertBlockAtCursor(ctx, blockNode, 3);

      // insertBlockAtCursor lands inside the new block; focus the default variant body.
      const variantDom = getVariantDomAtCursor(view);
      if (variantDom) {
        focusVariantDomBody(view, variantDom);
      }
    },
  }),
};
